import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '../../../../lib/formatCurrency';

const INITIAL_STATE = {
  catalogNumber: '',
  width: '',
  profile: '',
  rim: '',
  selectedSeasons: [],
  selectedBrands: [],
  minPrice: '',
  maxPrice: '',
  inStockOnly: false,
  // Vehicle search fields (Product API)
  vehicleVendor: '',
  vehicleModel: '',
  vehicleYear: '',
  vehicleVersion: '',
  vehicleFitment: '',
};

const useAdvancedSearchForm = (availableSeasons = [], availableBrands = []) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(INITIAL_STATE);

  const handleChange = useCallback((field, value) => {
    setQuery((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleVehicleChange = useCallback((field, value) => {
    setQuery((prev) => {
      const newState = { ...prev, [field]: value };

      // Reset subsequent fields when a parent field changes
      if (field === 'vehicleVendor') {
        newState.vehicleModel = '';
        newState.vehicleYear = '';
        newState.vehicleVersion = '';
        newState.vehicleFitment = '';
      } else if (field === 'vehicleModel') {
        newState.vehicleYear = '';
        newState.vehicleVersion = '';
        newState.vehicleFitment = '';
      } else if (field === 'vehicleYear') {
        newState.vehicleVersion = '';
        newState.vehicleFitment = '';
      } else if (field === 'vehicleVersion') {
        newState.vehicleFitment = '';
      }

      return newState;
    });
  }, []);

  const handleSeasonToggle = useCallback((seasonId) => {
    setQuery((prev) => ({
      ...prev,
      selectedSeasons: prev.selectedSeasons.includes(seasonId)
        ? prev.selectedSeasons.filter((id) => id !== seasonId)
        : [...prev.selectedSeasons, seasonId],
    }));
  }, []);

  const handleBrandToggle = useCallback((brandId) => {
    setQuery((prev) => ({
      ...prev,
      selectedBrands: prev.selectedBrands.includes(brandId)
        ? prev.selectedBrands.filter((id) => id !== brandId)
        : [...prev.selectedBrands, brandId],
    }));
  }, []);

  const handleClear = useCallback((onClearAll) => {
    if (window.confirm(t('confirmClearFields'))) {
      setQuery(INITIAL_STATE);
      onClearAll?.();
    }
  }, [t]);

  const buildQueryPreview = useCallback((vehicleData = {}) => {
    const { vendors = [], models = [], years = [], versions = [], fitments = [] } = vehicleData;
    const parts = [];

    if (query.catalogNumber) parts.push(`${t('catalogNumberShort')}: "${query.catalogNumber}"`);
    if (query.width || query.profile || query.rim) {
      parts.push(`${t('dimensions')}: ${[query.width, query.profile, query.rim].filter(Boolean).join('/')}`);
    }

    // Vehicle info
    const vehicleParts = [];
    if (query.vehicleVendor) {
      const vendor = vendors.find((v) => v.value === query.vehicleVendor);
      if (vendor) vehicleParts.push(vendor.label);
    }
    if (query.vehicleModel) {
      const model = models.find((m) => m.value === query.vehicleModel);
      if (model) vehicleParts.push(model.label);
    }
    if (query.vehicleYear) {
      const year = years.find((y) => y.value === query.vehicleYear);
      if (year) vehicleParts.push(year.label);
    }
    if (query.vehicleVersion) {
      const version = versions.find((v) => v.value === query.vehicleVersion);
      if (version) vehicleParts.push(version.label);
    }
    if (query.vehicleFitment) {
      const fitment = fitments.find((f) => f.value === query.vehicleFitment);
      if (fitment) vehicleParts.push(fitment.label);
    }
    if (vehicleParts.length > 0) {
      parts.push(`${t('vehicle')}: ${vehicleParts.join(' > ')}`);
    }

    if (query.selectedSeasons.length > 0) {
      const seasons = availableSeasons.filter((s) => query.selectedSeasons.includes(s.id)).map((s) => s.name);
      parts.push(`${t('seasons')}: ${seasons.join(', ')}`);
    }
    if (query.selectedBrands.length > 0) {
      const brands = availableBrands.filter((b) => query.selectedBrands.includes(b.value)).map((b) => b.label);
      parts.push(`${t('brands')}: ${brands.join(', ')}`);
    }
    if (query.minPrice || query.maxPrice) {
      parts.push(`${t('price')}: ${query.minPrice || '0'} - ${query.maxPrice || '∞'} ${getCurrencySymbol()}`);
    }
    if (query.inStockOnly) parts.push(t('onlyInStock'));

    return parts.length > 0 ? parts.join(' • ') : t('noFiltersSelected');
  }, [query, t, availableSeasons, availableBrands]);

  return { query, handleChange, handleVehicleChange, handleSeasonToggle, handleBrandToggle, handleClear, buildQueryPreview };
};

export default useAdvancedSearchForm;
